import React from "react";
import {
  Badge,
  Box,
  Button,
  IconButton,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  CustomModal,
  DeleteModal,
  InputControl,
  SimpleMenu,
  usePermission,
} from "../../../shared";
import { CreateOrEditStockTag } from "./CreateOrEditStockTag";
import { AddStockItemsDialog } from "./AddStockItemsDialog";
import { useStockItemTags } from "../hooks";
import { MoreHoriz } from "@mui/icons-material";
import leaf from "../../../assets/images/Icons/ingredient.svg";
import book from "../../../assets/images/Icons/Cookbook.svg";
import { Link } from "react-router-dom";

export const StockItemTags = () => {
  const {
    state,
    activeTag,
    handleSelectTag,
    handleOpenModal,
    handleCloseModal,
    handleTagNameChange,
    handleSubmit,
    handleDelete,
    handleDeleteItem,
    resetDeleteInfo,
    confirmDelete,
    isPending,
    handleOpenAddItems,
    handleCloseAddItems,
    handleAddItemsSubmit,
    isAddingItems,
    searchHandler,
    search,
  } = useStockItemTags();

  const canIngredientView = usePermission("ingredient-view");
  const canRecipeView = usePermission("recipe-view");

  return (
    <>
      <Stack sx={{ backgroundColor: "background.paper" }}>
        <Box sx={{ m: "8px" }}>
          <InputControl
            type="table-search"
            placeholder="Search"
            value={search}
            onChange={searchHandler}
            sx={{ minWidth: "400px", ml: "auto" }}
          />
        </Box>
        <Stack
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            height: "calc(100vh - 224px)",
          }}
        >
          <StockTagsPanel
            tags={state.tags}
            activeTagId={state.activeTagId}
            onSelectTag={handleSelectTag}
            handleOpenModal={handleOpenModal}
            handleDelete={handleDelete}
          />

          <StockItemsPanel
            key={state.activeTagId}
            activeTag={activeTag}
            handleOpenModal={handleOpenModal}
            handleOpenAddItems={handleOpenAddItems}
            handleDeleteItem={handleDeleteItem}
            canIngredientView={canIngredientView}
            canRecipeView={canRecipeView}
          />
        </Stack>
      </Stack>

      <CustomModal
        handleClose={handleCloseModal}
        open={state.isModalOpen}
        title={state.selectedTag ? "Edit Tag" : "Create Tag"}
        onSubmit={handleSubmit}
        isEdit={!!state.selectedTag}
        isDisableSubmit={!state.tagName.trim() || isPending}
        content={
          <CreateOrEditStockTag
            tagName={state.tagName}
            onChange={handleTagNameChange}
          />
        }
      />

      <CustomModal
        handleClose={handleCloseModal}
        open={state.selectedItem.open}
        title={"Items"}
        onSubmit={handleSubmit}
        isEdit={!!state.selectedItem?.id}
        isDisableSubmit={!state.tagName.trim() || isPending}
        content={
          <CreateOrEditStockTag
            tagName={state.tagName}
            onChange={handleTagNameChange}
          />
        }
      />

      <AddStockItemsDialog
        open={state.addItemsDialog.open}
        onClose={handleCloseAddItems}
        onAdd={handleAddItemsSubmit}
        existingItemIds={(activeTag?.subData || []).map((item) => item.id)}
        isAddingItems={isAddingItems}
      />

      <DeleteModal
        open={state.deleteInfo.show}
        handleClose={resetDeleteInfo}
        onConfirm={confirmDelete}
      />
    </>
  );
};

const StockTagsPanel = ({
  tags,
  activeTagId,
  onSelectTag,
  handleOpenModal,
  handleDelete,
}) => {
  return (
    <Stack sx={{ borderRight: 1, borderColor: "divider", overflow: "auto" }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor: "neutral.95",
          p: "8px",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Typography fontSize="16px" fontWeight={600}>
          Tags
        </Typography>
        <Button
          type="button"
          variant="contained"
          color="white"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          Stock Item Tag
        </Button>
      </Stack>

      {/* Body */}
      {tags.length ? (
        <Stack>
          {tags.map((tag, index) => {
            const isSelected = tag.id === activeTagId;
            return (
              <Stack
                key={tag.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                onClick={() => onSelectTag(tag.id)}
                sx={{
                  p: "8px",
                  cursor: "pointer",
                  borderBottom: index === tags.length ? 0 : 1,
                  borderColor: "divider",
                  color: isSelected ? "#ffff" : "text.primary",
                  backgroundColor: isSelected ? "primary.main" : "transparent",
                  "&:hover": {
                    backgroundColor: isSelected
                      ? "primary.main"
                      : "action.hover",
                  },
                }}
              >
                <Typography
                  fontSize="14px"
                  fontWeight={500}
                  display="flex"
                  alignItems="center"
                  gap="4px"
                >
                  {tag.name}
                  {tag.subData?.length > 0 && (
                    <StyledBadge
                      badgeContent={tag.subData.length}
                      background="#0909091A"
                      color={isSelected ? "#FFFF" : undefined}
                    />
                  )}
                </Typography>
                <SimpleMenu
                  row={{ original: tag, id: tag.id, index }}
                  editAction={(e) => handleOpenModal(e)}
                  handleDeleteModal={(e) => handleDelete(e.id)}
                  isDeletable={true}
                  isEditable={true}
                  customTrigger={({ onClick }) => (
                    <IconButton
                      onClick={onClick}
                      size="small"
                      sx={{ p: 0, m: 0 }}
                    >
                      <MoreHoriz
                        fontSize="small"
                        color={isSelected ? "white" : "gray"}
                      />
                    </IconButton>
                  )}
                />
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Stack
          sx={{
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            fontSize="14px"
            textAlign="center"
            p="16px"
            color="text.secondary"
          >
            There are no tags added yet. You need to add a tag first.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

const StockItemsPanel = ({
  activeTag,
  handleOpenAddItems,
  handleDeleteItem,
  canIngredientView,
  canRecipeView,
}) => {
  const subData = activeTag?.subData || [];

  return (
    <Stack sx={{ overflow: "auto" }}>
      {/* Header */}
      <Stack
        sx={{
          backgroundColor: "neutral.95",
          p: "8px",
          position: "sticky",
          top: 0,
          zIndex: 1,
          display: "grid",
          gridTemplateColumns: "1fr 100px 90px",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Typography fontSize="16px" fontWeight={600}>
          Stock Items
        </Typography>
        <Typography fontSize="16px" fontWeight={600}>
          Stock Code
        </Typography>
        <Button
          type="button"
          variant="contained"
          color="white"
          startIcon={<AddIcon />}
          onClick={() => handleOpenAddItems()}
          sx={{ justifySelf: "end" }}
        >
          Item
        </Button>
      </Stack>

      {/* Body */}
      {subData.length > 0 ? (
        <Stack>
          {subData.map((item, index) => {
            const canView =
              (item.type === "ingredient" && canIngredientView) ||
              (item.type === "recipe" && canRecipeView);

            return (
              <Stack
                key={item.id}
                sx={{
                  p: "8px",
                  display: "grid",
                  gridTemplateColumns: "1fr 100px 90px",
                  alignItems: "center",
                  gap: "8px",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Link
                  to={
                    canView
                      ? item.type === "recipe"
                        ? `/recipe/details/${item.type_id}/?basic-details`
                        : `/ingredients/details/${item.type_id}`
                      : ""
                  }
                  onClick={(e) => {
                    if (!canView) e.preventDefault();
                  }}
                  style={{
                    color: "inherit",
                    textDecoration: canView ? "underline" : "none",
                    cursor: canView ? "pointer" : "default",
                  }}
                >
                  <Typography
                    fontSize="14px"
                    display="flex"
                    alignItems="center"
                    gap="4px"
                  >
                    {item.type === "recipe" ? (
                      <img src={book} alt="ingredient" width={16} height={16} />
                    ) : (
                      <img src={leaf} alt="ingredient" width={16} height={16} />
                    )}
                    {item.name}
                  </Typography>
                </Link>

                <Typography fontSize="14px" color="text.secondary">
                  {item.stock_code ?? "-"}
                </Typography>
                <IconButton
                  size="small"
                  sx={{ p: 0, m: 0, mr: 1, justifySelf: "end" }}
                  onClick={() => handleDeleteItem(item.id)}
                >
                  <Box
                    component="img"
                    src="/icons/ic_trash_sm.png"
                    height={16}
                    width={16}
                  />
                </IconButton>
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Stack
          sx={{
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            fontSize="14px"
            textAlign="center"
            p="16px"
            color="text.secondary"
          >
            {!activeTag
              ? "Select a tag to see associated stock items."
              : "There are no stock items for this tag."}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

const StyledBadge = styled(Badge)(({ background, color = "text.primary" }) => ({
  "& .MuiBadge-badge": {
    right: 4,
    top: 10,
    padding: "0 4px",
    position: "relative",
    background,
    color,
    zIndex: 0,
  },
}));
